import { Text, Button, Image, Icon } from '@msc-b2c/shared-components';
import styles from './voyagerclubmembershipdetails.module.scss';
import { useState } from 'react';
import LevelProgressBar from './components/LevelProgressBar/LevelProgressBar';
import {
  isMobileDevice,
  managePlurals,
  replacePlaceholders,
  resolveDateFormat,
} from '@msc-b2c/shared-utils';
import { Level, VoyagerClubMembershipDetailsProps } from '@msc-b2c/interfaces/types';
import dayjs from 'dayjs';

export function VoyagerClubMembershipDetails({
  nameLabel,
  nameValue,
  cardNumberLabel,
  expirationDateLabel,
  pointTitle,
  pointsPlaceholderApp,
  pointsPlaceholderWeb,
  missingPointTextDescription,
  viewDetailsLabel,
  closeDetailsLabel,
  membershipDetails,
  membershipLevel,
  memberName,
  memberSurname,
  cardNumber,
  expirationDate,
  pointsFooterText,
  currentPoints = 0,
  pointsToNextLevel = 0,
  levels = [],
  notificationText,
  notificationIcon,
  notificationIconColor,
}: Readonly<VoyagerClubMembershipDetailsProps>) {
  const [isExpanded, setIsExpanded] = useState(false);

  // Find current level index and calculate next level info
  const currentLevelIndex = levels.findIndex(
    (level) => level.levelName?.toLowerCase() === membershipLevel?.toLowerCase()
  );
  const currentLevel = currentLevelIndex >= 0 ? levels[currentLevelIndex] : null;
  const isMaxLevel = currentLevelIndex === levels.length - 1;

  const findNextLevel = ({
    levels = [],
    currentPoints,
  }: {
    levels: Level[];
    currentPoints: number;
  }) => {
    const sorted = [...levels]
      .filter((l) => typeof l.pointsRequired === 'number')
      .sort((a, b) => (a.pointsRequired || 0) - (b.pointsRequired || 0));

    for (const lvl of sorted) {
      const required = lvl.pointsRequired || 0;
      if (required > currentPoints) {
        return lvl.levelName || '';
      }
    }

    // At max tier (or no levels)
    return '';
  };

  // API returns pointsToNextLevel as 1 less than the actual required points
  // We compensate by adding 1 to display the correct remaining points to users
  const correctedPointsToNextLevel = pointsToNextLevel + 1;

  // Format the description with bold values
  const formattedDescription =
    !isMaxLevel && missingPointTextDescription
      ? replacePlaceholders(
          managePlurals(correctedPointsToNextLevel, missingPointTextDescription),
          {
            missingPoints: `${correctedPointsToNextLevel}`,
            nextLevel: `${findNextLevel({ levels, currentPoints })}`,
          }
        )
      : '';

  const formattedNameValue = nameValue
    ? replacePlaceholders(nameValue, {
        name: memberName || '',
        surname: memberSurname || '',
      })
    : '';

  // Helper to render points string with special class for the points value
  const renderPointsText = (
    template: string,
    points: number,
    pointsClass: string,
    textClass: string
  ) => {
    if (!template) return null;
    const [before, after] = template.split('{points}');
    return (
      <span
        className={
          styles['cmp-voyagerclub-membership-details__points-header__points-value-container']
        }
      >
        {before && (
          <Text
            text={before.endsWith(' ') ? before + '\u00A0' : before}
            styleOption={textClass}
            tagHtml="span"
          />
        )}
        <Text
          text={`${after ? `${points.toLocaleString()}\u00A0` : `${points.toLocaleString()}`}`}
          styleOption={pointsClass}
          tagHtml="span"
        />
        {after && (
          <Text
            text={after}
            styleOption={textClass}
            tagHtml="span"
          />
        )}
      </span>
    );
  };

  return (
    <div className={styles['cmp-voyagerclub-membership-details']}>
      {/* Membership Level Title */}
      <div className={styles['cmp-voyagerclub-membership-details__title']}>
        <Text
          text={membershipLevel}
          tagHtml="span"
          styleOption={styles['cmp-voyagerclub-membership-details__text_title']}
        />
      </div>

      {/* Card and Details Layout */}
      <div className={styles['cmp-voyagerclub-membership-details__layout']}>
        {/* Membership Details Section */}
        <div className={styles['cmp-voyagerclub-membership-details__info']}>
          <Text
            text={membershipDetails}
            tagHtml="h4"
            styleOption={styles['cmp-voyagerclub-membership-details__info__title']}
          />

          <div className={styles['cmp-voyagerclub-membership-details__info-content']}>
            <div className={styles['cmp-voyagerclub-membership-details__info-row']}>
              <Text
                text={nameLabel}
                styleOption="cmp-text__medium bold cmp-voyagerclub-membership-details__info-row-title"
              />
              <Text
                text={formattedNameValue}
                styleOption={styles['cmp-voyagerclub-membership-details__info-row__value']}
              />
            </div>
            <div className={styles['cmp-voyagerclub-membership-details__info-row']}>
              <Text
                text={cardNumberLabel}
                styleOption="cmp-text__medium bold cmp-voyagerclub-membership-details__info-row-title"
              />
              <Text
                text={cardNumber}
                styleOption={styles['cmp-voyagerclub-membership-details__info-row__value']}
              />
            </div>

            <div className={styles['cmp-voyagerclub-membership-details__info-row']}>
              <Text
                text={expirationDateLabel}
                styleOption="cmp-text__medium bold cmp-voyagerclub-membership-details__info-row-title"
              />
              <Text
                text={expirationDate ? dayjs(expirationDate).format(resolveDateFormat()) : ''}
                styleOption={styles['cmp-voyagerclub-membership-details__info-row__value']}
              />
            </div>
          </div>
        </div>

        {/* Card Image */}
        <div className={styles['cmp-voyagerclub-membership-details__card']}>
          {currentLevel?.levelImageUrl && (
            <Image
              src={currentLevel.levelImageUrl}
              alt={currentLevel?.levelImageAlt ?? membershipLevel}
              styleOption={styles['cmp-voyagerclub-membership-details__card-image']}
            />
          )}
        </div>
      </div>

      {/* Points Overview Section */}
      <div className={styles['cmp-voyagerclub-membership-details__points']}>
        <div className={styles['cmp-voyagerclub-membership-details__points-header']}>
          <Text
            text={pointTitle}
            styleOption={styles['cmp-voyagerclub-membership-details__points-header__title']}
          />
          {isMobileDevice()
            ? renderPointsText(
                managePlurals(currentPoints, pointsPlaceholderApp || ''),
                currentPoints,
                styles['cmp-voyagerclub-membership-details__points-header__points-value-point'],
                styles['cmp-voyagerclub-membership-details__points-header__points-value']
              )
            : renderPointsText(
                managePlurals(currentPoints, pointsPlaceholderWeb || ''),
                currentPoints,
                styles['cmp-voyagerclub-membership-details__points-header__points-value-point'],
                styles['cmp-voyagerclub-membership-details__points-header__points-value']
              )}
        </div>

        {!isMaxLevel && formattedDescription && (
          <Text
            text={formattedDescription}
            styleOption="cmp-voyagerclub-membership-details__points-description"
          />
        )}

        {/* Progress section: mobile collapsed by default, desktop always visible */}
        {levels.length > 0 && (
          <div
            className={[
              styles['cmp-voyagerclub-membership-details__progress-section'],
              isExpanded
                ? styles['cmp-voyagerclub-membership-details__progress-section--expanded']
                : '',
            ].join(' ')}
          >
            <LevelProgressBar
              levels={levels}
              currentPoints={currentPoints}
            />
          </div>
        )}

        {!isMobileDevice() && pointsFooterText && (
          <Text
            text={pointsFooterText}
            styleOption="cmp-voyagerclub-membership-details__points-footer"
          />
        )}

        {/* Mobile-only toggle */}
        {isMobileDevice() && levels.length > 0 && (
          <div className={styles['cmp-voyagerclub-membership-details__view-details']}>
            <Button
              text={isExpanded ? closeDetailsLabel : viewDetailsLabel}
              icon={isExpanded ? 'up' : 'down'}
              iconAriaLabel={isExpanded ? closeDetailsLabel : viewDetailsLabel}
              accessibilityLabel={isExpanded ? closeDetailsLabel : viewDetailsLabel}
              onClick={() => setIsExpanded((v) => !v)}
              styleOption={styles['cmp-voyagerclub-membership-details__toggle-button']}
            />
          </div>
        )}
      </div>
      {notificationText && (
        <div className={styles['cmp-voyagerclub-membership-details__tier-notification']}>
          {notificationIcon && (
            <Icon
              icon={notificationIcon}
              color={notificationIconColor}
              ariaHidden={true}
              styleOption={styles['cmp-voyagerclub-membership-details__tier-notification-icon']}
            />
          )}
          <Text
            text={notificationText}
            styleOption={styles['cmp-voyagerclub-membership-details__tier-notification-text']}
          />
        </div>
      )}
    </div>
  );
}

export default VoyagerClubMembershipDetails;
